import { FormControl, FormHelperText, FormLabel } from "@chakra-ui/form-control";
import { Tooltip } from "@chakra-ui/react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  InlineNotification,
  Input,
  Link,
  Select,
  Tag,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link as ReactLink } from "react-router";
import { useCommunityFeatureFlagStore } from "../../storez/CommunityFeatureFlagStore";
import { useDeviceOptionsStore } from "../../storez/DeviceOptionsStore";
import { useFeatureFlagStore } from "../../storez/FeatureFlagStore";
import { Language, useAccountStore } from "../../storez/AccountStore";
import { ResponseError } from "../../types/Api";
import { CommunityFlags, CommunityFlagsEquals } from "../../types/Community";
import { DeviceOptions, DeviceOptionsEquals } from "../../types/Device";
import { FeatureFlags, FeatureFlagsEquals } from "../../types/FeatureFlags";
import SectionHeading from "../header/components/SectionHeading";
import CardBasedPage from "../templates/CardBasedPage";

type LanguageOption = { value: Language; text: string };

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { text: "English UK", value: Language.EN_UK },
  { text: "Debugging", value: Language.EN_XX },
];

enum ChatSupportType {
  ZENDESK = "zendesk",
  ZENDESK_SUNSHINE = "zendesk_sunshine",
  FORETHOUGHT_AI = "forethought_ai",
  NONE = "none",
}

type ChatSupportOption = { text: string; value: ChatSupportType };

const CHAT_SUPPORT_OPTIONS: ChatSupportOption[] = [
  { text: "Zendesk", value: ChatSupportType.ZENDESK },
  { text: "Zendesk Sunshine", value: ChatSupportType.ZENDESK_SUNSHINE },
  { text: "Forethought AI", value: ChatSupportType.FORETHOUGHT_AI },
  { text: "None", value: ChatSupportType.NONE },
];

const DASHBOARD_SECTION_DISABLE_FLAGS = new Set<keyof CommunityFlags>([
  "disableParentTakeUpDashboardSection",
  "disableSignUpsOverTimeDashboardSection",
  "disableCommunicationsDashboardSection",
  "disableParentEngagementDashboardSection",
]);

const DASHBOARD_SECTION_FLAG_KEYS = [...DASHBOARD_SECTION_DISABLE_FLAGS];

const ManageDeviceFeatureFlags: React.FC = () => {
  const { t } = useTranslation();
  const { errorToast, successToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [deviceOptions, setDeviceOptions] = useState<DeviceOptions>({});
  const [communityFlags, setCommunityFlags] = useState<CommunityFlags>({});
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({});

  const [accountSettings, setUserLanguage] = useAccountStore(useCallback((state) => [state.settings, state.setLanguage] as const, []));

  const [storedDeviceOptions, fetchDeviceOptions, saveDeviceOptions] = useDeviceOptionsStore(
    useCallback((state) => [state.options, state.fetch, state.save] as const, [])
  );

  const [storedFeatureFlags, fetchFeatureFlags, saveFeatureFlags] = useFeatureFlagStore(
    useCallback((state) => [state.flags, state.fetch, state.save] as const, [])
  );

  const [storedCommunityFlags, fetchCommunityFlags, saveCommunityFlags] = useCommunityFeatureFlagStore(
    useCallback((state) => [state.flags, state.fetch, state.save] as const, [])
  );

  useEffect(() => {
    void Promise.all([
      fetchDeviceOptions().then((options) => setDeviceOptions({ ...options })),
      fetchFeatureFlags().then((flags) => setFeatureFlags({ ...flags })),
      fetchCommunityFlags().then((flags) => setCommunityFlags({ ...flags })),
    ]).then(
      () => {
        setLoading(false);
      },
      (err: ResponseError) => {
        errorToast({
          title: t("Please try again"),
          description: t(err.message),
          isClosable: true,
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDeviceOptions, fetchFeatureFlags, fetchCommunityFlags]);

  const handleDeviceOptionsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.id;
    const value = event.target.checked;
    setDeviceOptions({ ...deviceOptions, [name]: value });
  };

  const handleCommunityFlagsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.id;
    const value = event.target.checked;

    if (DASHBOARD_SECTION_DISABLE_FLAGS.has(name as keyof CommunityFlags)) {
      setCommunityFlags({
        ...communityFlags,
        // Preserve "Enable ..." wording while storing backend "disable..." semantics.
        [name]: !value,
      });
    } else if (name === "enableCommunity" && !value) {
      setCommunityFlags({
        ...communityFlags,
        enableCommunity: false,
        enableCommunityDashboard: false,
        enableDelegation: false,
        enableDelegationByInclusion: false,
      });
    } else if (name === "enableDelegation" && !value) {
      setCommunityFlags({
        ...communityFlags,
        enableDelegation: false,
        enableDelegationByInclusion: false,
      });
    } else {
      setCommunityFlags({ ...communityFlags, [name]: value });
    }
  };

  const handleFeatureFlagsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const name = event.target.id;
    const value = event.target.checked;

    if (name && value !== undefined) {
      setFeatureFlags({ ...featureFlags, [name]: value });
    }
  };

  const handleLanguageChange = (selected: LanguageOption | null) => {
    if (selected) {
      setUserLanguage(selected.value);
    }
  };

  const handleChatSupportChange = (selected: ChatSupportOption | null) => {
    setDeviceOptions((options) => ({
      ...options,
      allow_support_zendesk: selected?.value === ChatSupportType.ZENDESK,
      allow_support_school_manager: selected?.value === ChatSupportType.ZENDESK_SUNSHINE,
      allow_support_forethought_ai: selected?.value === ChatSupportType.FORETHOUGHT_AI,
    }));
  };

  const handleSave = () => {
    setSaving(true);

    const promises = [];

    if (!FeatureFlagsEquals(featureFlags, storedFeatureFlags)) {
      promises.push(saveFeatureFlags(featureFlags).then((saved) => setFeatureFlags(saved)));
    }

    if (!DeviceOptionsEquals(deviceOptions, storedDeviceOptions)) {
      // device options API supports partial update => save only the
      // properties that can be changed on the feature flags page
      promises.push(
        saveDeviceOptions({
          display_machine_name: deviceOptions.display_machine_name ?? false,
          allow_extension_connections: deviceOptions.allow_extension_connections ?? false,
          allow_support_school_manager: deviceOptions.allow_support_school_manager ?? false,
          allow_support_zendesk: deviceOptions.allow_support_zendesk ?? false,
          allow_support_forethought_ai: deviceOptions.allow_support_forethought_ai ?? false,
        }).then((saved) => setDeviceOptions(saved))
      );
    }

    if (!CommunityFlagsEquals(communityFlags, storedCommunityFlags)) {
      promises.push(saveCommunityFlags(communityFlags).then((saved) => setCommunityFlags(saved)));
    }

    void Promise.all(promises)
      .then(
        () => {
          successToast({
            title: t("Update successful"),
            description: t("Feature flags have been successfully updated"),
            isClosable: true,
          });
        },
        (err: ResponseError) => {
          errorToast({
            title: t("Please try again"),
            description: t(err.message),
            isClosable: true,
          });
        }
      )
      .finally(() => setSaving(false));
  };

  const selectedLanguageOption = useMemo(() => {
    return LANGUAGE_OPTIONS.find((o) => o.value === accountSettings.language);
  }, [accountSettings]);

  const selectedChatSupportOption = useMemo(() => {
    if (Object.keys(deviceOptions).length === 0) return undefined;

    let chatSupportType = ChatSupportType.NONE;
    if (deviceOptions.allow_support_zendesk) {
      chatSupportType = ChatSupportType.ZENDESK;
    } else if (deviceOptions.allow_support_school_manager) {
      chatSupportType = ChatSupportType.ZENDESK_SUNSHINE;
    } else if (deviceOptions.allow_support_forethought_ai) {
      chatSupportType = ChatSupportType.FORETHOUGHT_AI;
    }
    return CHAT_SUPPORT_OPTIONS.find((o) => o.value === chatSupportType);
  }, [deviceOptions]);

  const hasChanges =
    (Object.keys(deviceOptions).length > 0 && !DeviceOptionsEquals(deviceOptions, storedDeviceOptions)) ||
    (Object.keys(featureFlags).length > 0 && !FeatureFlagsEquals(featureFlags, storedFeatureFlags)) ||
    (Object.keys(communityFlags).length > 0 && !CommunityFlagsEquals(communityFlags, storedCommunityFlags));
  const isParentDashboardEnabled = !!communityFlags.enableCommunityDashboard;
  const enabledDashboardSectionCount = DASHBOARD_SECTION_FLAG_KEYS.filter((key) => !communityFlags[key]).length;
  const isLastEnabledDashboardSection = (disableFlagKey: keyof CommunityFlags): boolean =>
    isParentDashboardEnabled && !communityFlags[disableFlagKey] && enabledDashboardSectionCount <= 1;

  return (
    <CardBasedPage
      title={t("Feature Flags (Support Admin Only)")}
      breadcrumbs={[
        { title: t("Debugging"), url: "/managedevice", isActive: false },
        { title: t("Feature Flags"), isActive: true },
      ]}
    >
      <Box>
        <Box p="sp24">
          <Box>
            <SectionHeading title={t("Miscellaneous")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="display_machine_name"
                  isChecked={deviceOptions.display_machine_name}
                  isDisabled={loading || saving}
                  onChange={handleDeviceOptionsChange}
                >
                  {t("Display Machine Name")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Machine Name' column on Statistics > Users > User Timeline page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-security-groups-menu-item"
                  isChecked={featureFlags["show-security-groups-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Security Groups Page")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Configuration > Objects > Security Groups menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="allow_extension_connections"
                  isChecked={deviceOptions.allow_extension_connections}
                  isDisabled={loading || saving}
                  onChange={handleDeviceOptionsChange}
                >
                  {t("Browser Extension Connection Reporting")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Allows connection information while inside network.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="filtering_agents_custom_http_headers"
                  isChecked={featureFlags.filtering_agents_custom_http_headers}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Enable Custom Headers (Product Initiative)")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables Mac agents to inject custom headers specified in 'allow' filtering policies.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="config_agents_filtering_bypass"
                  isChecked={featureFlags.config_agents_filtering_bypass}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Bypass Filtering")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Bypass Filtering Groups' option on Configuration > Mobile Agent page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="unified_observability"
                  isChecked={featureFlags.unified_observability}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Unified Observability")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables Unified Observability.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="device_specs"
                  isChecked={featureFlags.device_specs}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Device Specs")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables Windows and Mac agents to include hardware information in connection reports.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-manage-user-password"
                  isChecked={featureFlags["show-manage-user-password"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Password Field for Local Users")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Set Password' button for local users on Configuration > Users > Manage User page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Flex alignItems="center" gap="sp8">
                  <Checkbox
                    id="configure_restrictive_mode"
                    isChecked={featureFlags.configure_restrictive_mode}
                    isDisabled={loading || saving}
                    onChange={handleFeatureFlagsChange}
                  >
                    {t("Configure Captive Portal Restrictive Mode")}
                  </Checkbox>
                  <Tag size="compact" variant="orange" tagLabel={t("Dev Only")} />
                </Flex>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Restrictive Mode' option on Configuration > Authentication > Captive Portal page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="hide-red-flags"
                  isChecked={featureFlags["hide-red-flags"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Hide Red Flags")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Hides 'Red Flags' section on Statistics > Dashboard page and Cyber Safety > Red Flags menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="hide-dashboard-teacher-signin"
                  isChecked={featureFlags["hide-dashboard-teacher-signin"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Hide Teacher Sign In button on Dashboard")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Hides 'Sign In as Teacher' button on Statistics > Dashboard page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-clever-sync-menu-item"
                  isChecked={featureFlags["show-clever-sync-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Enable Clever Sync Menu")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Show 'Clever' section on Configuration > Authentication > Clever")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-ldap-sync-menu-item"
                  isChecked={featureFlags["show-ldap-sync-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Enable LDAP Sync Menu")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Show 'LDAP' section on Configuration > Authentication > LDAP")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="hide-status-page-banner"
                  isChecked={featureFlags["hide-status-page-banner"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Hide Status Page Banner")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t(
                    "Hides notification banner displayed in Linewize Filter and Classroom Manager when incident or maintenance notice is active on "
                  )}
                  <Link href="https://status.linewize.net" target="_blank">
                    status.linewize.net
                  </Link>
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-high-availability-menu-item"
                  isChecked={featureFlags["show-high-availability-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show High Availability Page")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Configuration > Networking > High Availability menu item.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Miscellaneous--> */}

          <Box>
            <SectionHeading title={t("Chat Support")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Flex gap="sp8">
                  <FormLabel mt="sp12" htmlFor="chatSupport-input">
                    {t("Chat Support")}
                  </FormLabel>
                  <Flex direction="column">
                    {selectedChatSupportOption && !loading ? (
                      <Select
                        id="chatSupport"
                        items={CHAT_SUPPORT_OPTIONS}
                        selectedItem={selectedChatSupportOption}
                        onChange={(e) => handleChatSupportChange(e as ChatSupportOption)}
                        disabled={loading || saving}
                      />
                    ) : (
                      /* Select component does not refresh the initial selected value once it's set, */
                      /* so as a workaround, display a placeholder box until the value is available */
                      <Input isDisabled={true} placeholder={selectedChatSupportOption?.text} />
                    )}
                    <FormHelperText mt="sp8" color="neutrals.200">
                      {t("Displays the selected support widget in bottom-right corner for all users of this device.")}
                    </FormHelperText>
                  </Flex>
                </Flex>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Chat Support--> */}

          <Box>
            <SectionHeading title={t("Classroom Manager")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="agent-p2p-screenshots"
                  isChecked={featureFlags["agent-p2p-screenshots"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Live View for Windows and Mac")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables Live View in Classroom Manager for Windows and Mac agents.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="classwize-teacher-student-chat"
                  isChecked={featureFlags["classwize-teacher-student-chat"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Teacher-Student Two-Way Chat")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Chromebooks and Windows Companion mode only.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="shr-enabled"
                  isChecked={featureFlags["shr-enabled"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Screenshot History Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables collection of screenshot history and 'Screenshot History' report in Classroom Manager.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Classwize--> */}

          <Box>
            <SectionHeading title={t("Connection Search Report")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="connection-search-report"
                  isChecked={featureFlags["connection-search-report"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Connection Search Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays top-level Reports menu item.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Connection Search Report--> */}

          <Box>
            <SectionHeading title={t("Student Reports")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-student-wellbeing-menu-item"
                  isChecked={featureFlags["show-student-wellbeing-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Student Wellbeing Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Student Wellbeing menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-student-behaviour-menu-item"
                  isChecked={featureFlags["show-student-behaviour-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Student Behaviour Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Student Behaviour menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-student-activity-menu-item"
                  isChecked={featureFlags["show-student-activity-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Student Activity Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Student Activity menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-distracted-student-menu-item"
                  isChecked={featureFlags["show-distracted-student-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Distracted Student Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Digital Distraction menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-activity-compliance-menu-item"
                  isChecked={featureFlags["show-activity-compliance-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Activity and Compliance Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Activity and Compliance menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-applications-usage-menu-item"
                  isChecked={featureFlags["show-applications-usage-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Applications Usage Report")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Reports > Applications Usage menu item.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Student Reports--> */}

          <Box>
            <SectionHeading title={t("Parent")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving || (!communityFlags.enableCommunity && !deviceOptions.display_name)}>
                <Checkbox
                  id="enableCommunity"
                  isChecked={communityFlags.enableCommunity}
                  isDisabled={loading || saving || (!communityFlags.enableCommunity && !deviceOptions.display_name)}
                  onChange={handleCommunityFlagsChange}
                >
                  {t("Enable Parent")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Parent menu item, allows importing parent data, and enables Qustodio integration.")}
                </FormHelperText>

                {!loading && !communityFlags.enableCommunity && !deviceOptions.display_name && (
                  <Box mt="sp8" ml="sp24" width="fit-content">
                    <InlineNotification
                      status="info"
                      notificationDescription={
                        <Box pr="sp8" color="text.title">
                          {t("Parent cannot be enabled until an Organisation Name is added in")}{" "}
                          <Link as={ReactLink} to="/managedevice/settings/device">
                            {t("Debugging > Device Options")}
                          </Link>
                          {"."}
                        </Box>
                      }
                    />
                  </Box>
                )}

                {!loading && communityFlags.enableCommunity && !deviceOptions.display_name && (
                  <Box mt="sp8" ml="sp24" width="fit-content">
                    <InlineNotification
                      status="warning"
                      notificationDescription={
                        <Box pr="sp8" color="text.title">
                          {t("Parent requires Organisation Name to be set in Debugging > Device Options.")}
                        </Box>
                      }
                    />
                  </Box>
                )}
              </FormControl>

              <FormControl isDisabled={loading || saving || (!communityFlags.enableCommunity && !communityFlags.enableCommunityDashboard)}>
                <Checkbox
                  id="enableCommunityDashboard"
                  isChecked={communityFlags.enableCommunityDashboard}
                  isDisabled={loading || saving || (!communityFlags.enableCommunity && !communityFlags.enableCommunityDashboard)}
                  onChange={handleCommunityFlagsChange}
                >
                  {t("Enable Parent Dashboard")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Parent > Dashboard menu item.")}
                </FormHelperText>
              </FormControl>

              <Box ml="sp24">
                <Flex direction="column" gap="sp16">
                  <FormControl isDisabled={loading || saving || !isParentDashboardEnabled}>
                    <Checkbox
                      id="disableParentTakeUpDashboardSection"
                      isChecked={!communityFlags.disableParentTakeUpDashboardSection}
                      isDisabled={
                        loading ||
                        saving ||
                        !isParentDashboardEnabled ||
                        isLastEnabledDashboardSection("disableParentTakeUpDashboardSection")
                      }
                      onChange={handleCommunityFlagsChange}
                    >
                      {t("Enable Parent Take Up Dashboard Section")}
                    </Checkbox>
                    <FormHelperText ml="sp24" color="neutrals.200">
                      {t("Displays the Parent Take Up section on Parent > Dashboard.")}
                    </FormHelperText>
                  </FormControl>

                  <FormControl isDisabled={loading || saving || !isParentDashboardEnabled}>
                    <Checkbox
                      id="disableSignUpsOverTimeDashboardSection"
                      isChecked={!communityFlags.disableSignUpsOverTimeDashboardSection}
                      isDisabled={
                        loading ||
                        saving ||
                        !isParentDashboardEnabled ||
                        isLastEnabledDashboardSection("disableSignUpsOverTimeDashboardSection")
                      }
                      onChange={handleCommunityFlagsChange}
                    >
                      {t("Enable Sign Ups Over Time Dashboard Section")}
                    </Checkbox>
                    <FormHelperText ml="sp24" color="neutrals.200">
                      {t("Displays the Sign-Ups Over Time section on Parent > Dashboard.")}
                    </FormHelperText>
                  </FormControl>

                  <FormControl isDisabled={loading || saving || !isParentDashboardEnabled}>
                    <Checkbox
                      id="disableCommunicationsDashboardSection"
                      isChecked={!communityFlags.disableCommunicationsDashboardSection}
                      isDisabled={
                        loading ||
                        saving ||
                        !isParentDashboardEnabled ||
                        isLastEnabledDashboardSection("disableCommunicationsDashboardSection")
                      }
                      onChange={handleCommunityFlagsChange}
                    >
                      {t("Enable Communications Dashboard Section")}
                    </Checkbox>
                    <FormHelperText ml="sp24" color="neutrals.200">
                      {t("Displays the Communications section on Parent > Dashboard.")}
                    </FormHelperText>
                  </FormControl>

                  <FormControl isDisabled={loading || saving || !isParentDashboardEnabled}>
                    <Checkbox
                      id="disableParentEngagementDashboardSection"
                      isChecked={!communityFlags.disableParentEngagementDashboardSection}
                      isDisabled={
                        loading ||
                        saving ||
                        !isParentDashboardEnabled ||
                        isLastEnabledDashboardSection("disableParentEngagementDashboardSection")
                      }
                      onChange={handleCommunityFlagsChange}
                    >
                      {t("Enable Parent Engagement Dashboard Section")}
                    </Checkbox>
                    <FormHelperText ml="sp24" color="neutrals.200">
                      {t("Displays the Parent Engagement section on Parent > Dashboard.")}
                    </FormHelperText>
                  </FormControl>
                </Flex>
              </Box>

              <FormControl isDisabled={loading || saving || !communityFlags.enableCommunity || storedCommunityFlags?.enableWeeklyCampaign}>
                <Checkbox
                  id="enableWeeklyCampaign"
                  isChecked={communityFlags.enableWeeklyCampaign}
                  isDisabled={loading || saving || !communityFlags.enableCommunity || storedCommunityFlags?.enableWeeklyCampaign}
                  onChange={handleCommunityFlagsChange}
                >
                  {t("Enable Weekly Campaign")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Send Weekly Reports' option on Parent > Parental Controls page.")}
                </FormHelperText>
                {!loading && (communityFlags.enableCommunity || communityFlags.enableWeeklyCampaign) && (
                  <Box mt="sp8" ml="sp24" width="fit-content">
                    <InlineNotification
                      status="info"
                      notificationDescription={
                        <Box pr="sp8" color="text.title">
                          {t(
                            "Once the Weekly Campaign feature flag is enabled, it cannot be disabled by the user. Contact Community PM to disable it."
                          )}
                        </Box>
                      }
                    />
                  </Box>
                )}
              </FormControl>

              <FormControl isDisabled={loading || saving || (!communityFlags.enableCommunity && !communityFlags.enableDelegation)}>
                <Checkbox
                  id="enableDelegation"
                  isChecked={communityFlags.enableDelegation}
                  isDisabled={loading || saving || (!communityFlags.enableCommunity && !communityFlags.enableDelegation)}
                  onChange={handleCommunityFlagsChange}
                >
                  {t("Enable Extended Rules")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays 'Extended Rules' section on Parent > Parental Controls page.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving || !communityFlags.enableDelegation}>
                <Checkbox
                  id="enableDelegationByInclusion"
                  isChecked={communityFlags.enableDelegationByInclusion}
                  isDisabled={loading || saving || !communityFlags.enableDelegation}
                  onChange={handleCommunityFlagsChange}
                >
                  {t("Enable Extended Rules By Inclusion")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Limits 'Extended Rules' availability to users and/or groups selected on Parent > Parental Controls page.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Community--> */}

          <Box>
            <SectionHeading title={t("Filtering")} />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="show-cloudsafe-menu-item"
                  isChecked={featureFlags["show-cloudsafe-menu-item"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Show Cloudsafe Network Page")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Displays Filtering > Cloud Safe Network menu item.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Flex alignItems="center" gap="sp8">
                  <Checkbox
                    id="companion-mode-lite-enabled"
                    isChecked={featureFlags["companion-mode-lite-enabled"]}
                    isDisabled={loading || saving}
                    onChange={handleFeatureFlagsChange}
                  >
                    {t("Companion Mode Lite")}
                  </Checkbox>
                  <Tag size="compact" variant="orange" tagLabel={t("Troubleshooting Only")} />
                </Flex>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Disables DNS filtering agent plugin when in Companion Mode.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Flex alignItems="center" gap="sp8">
                  <Checkbox
                    id="qoria_dns"
                    isChecked={featureFlags["qoria_dns"]}
                    isDisabled={loading || saving}
                    onChange={handleFeatureFlagsChange}
                  >
                    {t("Qoria DNS")}
                  </Checkbox>
                  <Tag size="compact" variant="brand" tagLabel={t("Companion Mode Only")} />
                </Flex>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Uses Qoria DNS as fallback filtering solution when in Companion Mode.")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Flex alignItems="center" gap="sp8">
                  <Checkbox
                    id="ondevice_dns_resolution"
                    isChecked={featureFlags["ondevice_dns_resolution"]}
                    isDisabled={loading || saving}
                    onChange={handleFeatureFlagsChange}
                  >
                    {t("On-Device DNS Resolution")}
                  </Checkbox>
                  <Tag size="compact" variant="brand" tagLabel={t("BETA")} />
                </Flex>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Uses a device's DNS name servers to resolve allowed requests")}
                </FormHelperText>
              </FormControl>

              <FormControl isDisabled={loading || saving}>
                <Checkbox
                  id="content-aware-text-analysis"
                  isChecked={featureFlags["content-aware-text-analysis"]}
                  isDisabled={loading || saving}
                  onChange={handleFeatureFlagsChange}
                >
                  {t("Content Aware Text Analysis")}
                </Checkbox>
                <FormHelperText ml="sp24" color="neutrals.200">
                  {t("Enables Content Aware text analysis feature.")}
                </FormHelperText>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Filtering--> */}

          <Box>
            <SectionHeading
              title={t("Experimental UI/UX")}
              icon={
                <Tooltip
                  variant="dark"
                  placement="top-start"
                  hasArrow={true}
                  maxWidth="336px"
                  label={t("The experimental options may not properly function, or not function at all. Caution is advised.")}
                  offset={[-16, 8]}
                >
                  <Flex>
                    <Icon variant="solid" color="brand.400" fontSize="lg" icon="fa-info-circle" />
                  </Flex>
                </Tooltip>
              }
            />

            <Flex px="sp12" py="sp16" direction="column" gap="sp16">
              <FormControl isDisabled={loading || saving}>
                <Flex gap="sp8">
                  <FormLabel mt="sp12" htmlFor="userLanguage-input">
                    {t("Language")}
                  </FormLabel>
                  <Flex direction="column">
                    <Flex gap="sp8">
                      {selectedLanguageOption && !loading ? (
                        <Select
                          id="userLanguage"
                          items={LANGUAGE_OPTIONS}
                          selectedItem={selectedLanguageOption}
                          onChange={(e) => handleLanguageChange(e as LanguageOption)}
                          disabled={loading || saving}
                        />
                      ) : (
                        /* Select component does not refresh the initial selected value once it's set, */
                        /* so as a workaround, display a placeholder box until the value is available */
                        <Input isDisabled={true} placeholder={selectedLanguageOption?.text} />
                      )}
                      <Box mt="sp8">
                        <Tag size="compact" variant="orange" tagLabel={t("Dev Only")} />
                      </Box>
                    </Flex>
                    <FormHelperText mt="sp8" color="neutrals.200">
                      {t("Changes the language of the app for the current user.")}
                    </FormHelperText>
                  </Flex>
                </Flex>
              </FormControl>
            </Flex>
          </Box>
          {/* <!--./Experimental UI/UX--> */}
        </Box>
        <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
          <Button variant="primary" disabled={loading || saving || !hasChanges} onClick={handleSave}>
            {saving ? t("Saving..") : hasChanges ? t("Save") : t("Saved")}
          </Button>
        </Box>
      </Box>
    </CardBasedPage>
  );
};

export default ManageDeviceFeatureFlags;
